import React, { useMemo } from 'react';
import { BannerHero } from 'components/banner/bannerHero';
import { DefaultCarousel } from 'components/carousels/defaultCarousel';
import { SuggestionCarousel } from 'components/carousels/suggestionCarousel';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { InternalMenu } from 'components/internalMenu';
import { Skeleton } from 'components/skeleton';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { VisualComposer } from 'components/visualComposer';
import { createPageComposition } from 'components/visualComposer/utils/utils';
import { useGetCasinoQuery, useGetMightLikesQuery, useLazyGetQuickSlotQuery } from 'features/api/apiSlice';
import { useGetPageBannersQuery } from 'features/api/promoEngineApiSlice';
import { useInternalSession, useIsomorphicLayoutEffect } from 'hooks';
import { useGetMightLikesByList } from 'hooks/useGetAllMightLikes';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { BANNER_ON_PAGE_PROMO, HERO_BANNER_CMS, HERO_BANNER_PROMO } from 'lib/datoCms/constants';
import { CasinoTopPageQuery } from 'lib/datoCms/queries/getCasinoTopPage';
import { handleBanner } from 'lib/datoCms/utils';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { Category } from 'types/categoryEnum';
import { GamesSkillGameDto } from 'types/swagger';
import { FAVORITE_LIST, isServerSide } from 'utility/constant';
import { conditionsToVisualizeBanner, gtag } from 'utility/functions';
import { getGamesSlotByType } from 'components/page-content/slot-top/utils/utils';
import { SlotCardProps } from 'components/cards/slotCard/SlotCard';

type Props = {
  casinoTopPage: CasinoTopPageQuery['casinoTopPage'];
  choseFromSnaiCardSearch: Array<SlotCardProps>;
};

const PageContent: NextPage<Props> = ({ casinoTopPage, choseFromSnaiCardSearch }: Props) => {
  const { isLoading, data: casinoCatalogResponseApi } = useGetCasinoQuery();
  const { isAuthenticated: isLoggedIn } = useInternalSession();
  const favoriteLabel = useGetLabelByKey('preferiti')!;

  const { asPath } = useRouter();

  // promo engine
  const { isError: isErrorPromoEngine, data: promoEngine } = useGetPageBannersQuery(String(casinoTopPage.id), {
    skip: isServerSide,
    refetchOnMountOrArgChange: true,
  });

  // TODO -> useLazy
  const [getQuickSlots, { data: quickSlotResponseApi, isLoading: quickSlotIsLoading }] = useLazyGetQuickSlotQuery();

  const gamesFavorite = useMemo(
    () => casinoCatalogResponseApi && getGamesSlotByType(casinoCatalogResponseApi, FAVORITE_LIST),
    [casinoCatalogResponseApi]
  );
  const { data: mightLikesResposeApi } = useGetMightLikesQuery(casinoTopPage.slug);
  let mightLikesList = useGetMightLikesByList(mightLikesResposeApi?.cardList);
  const labelSearch = useGetLabelByKey('search')!;

  const { visualComposer } = casinoTopPage;

  const listQuickSlot = useMemo(() => {
    if (quickSlotResponseApi) return quickSlotResponseApi.games;
    return;
  }, [quickSlotResponseApi]);

  const pageBannerList = useMemo(() => {
    if (
      conditionsToVisualizeBanner(casinoTopPage, {
        type: BANNER_ON_PAGE_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      })
    ) {
      return promoEngine!.pageBannerList;
    }
    return undefined;
  }, [casinoTopPage, promoEngine, isErrorPromoEngine]);

  const newVisualComposer = useMemo(() => {
    const newCasinoCataloResponseApi = {
      ...casinoCatalogResponseApi!,
      listQuickSlot,
      pageBannerList,
    };

    return (
      visualComposer &&
      newCasinoCataloResponseApi &&
      createPageComposition<GamesSkillGameDto>({
        visualComposer: visualComposer!,
        catalog: newCasinoCataloResponseApi,
        slug: casinoTopPage.slug,
      }).map((e) => e!)
    );
  }, [casinoCatalogResponseApi, casinoTopPage.slug, listQuickSlot, pageBannerList, visualComposer]);

  useIsomorphicLayoutEffect(() => {
    if (isServerSide) return;
    getQuickSlots();
  }, [asPath, getQuickSlots]);

  return (
    <React.Fragment>
      {casinoTopPage.title && (
        <HeaderTitleWithSearch
          title={casinoTopPage.title}
          category={Category.Casino}
          labelSearch={labelSearch}
          cardChoseFromSnai={choseFromSnaiCardSearch}
          hasPaddingLeft
        />
      )}
      {conditionsToVisualizeBanner(casinoTopPage, {
        type: HERO_BANNER_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      }) && <BannerHero banners={promoEngine!.heroBannerList} />}
      {conditionsToVisualizeBanner(casinoTopPage, {
        type: HERO_BANNER_CMS,
      }) && <BannerHero banners={handleBanner(casinoTopPage.heroBanner[0].banners!)} />}
      {casinoTopPage.internalMenu && (
        <InternalMenu
          items={casinoTopPage.internalMenu[0].linkList}
          gtagAction={(tab: string) => gtag({ section: 'Casinò', detail: `Click ${tab}`, event: 'Tab Click' }, true)}
        />
      )}

      {isLoading && quickSlotIsLoading ? (
        <Skeleton hasTitle hasPadding rows={3} />
      ) : (
        <React.Fragment>
          {gamesFavorite && gamesFavorite.length > 0 && isLoggedIn && (
            <DefaultCarousel title={favoriteLabel} typeOfItem="casino" listItem={gamesFavorite} />
          )}
          {newVisualComposer && <VisualComposer type="casino" components={newVisualComposer} />}
        </React.Fragment>
      )}
      {mightLikesList && mightLikesList.length > 0 && <SuggestionCarousel listItem={mightLikesList} />}
      {casinoTopPage.tabsWithAccordion && <TabsWithAccordion {...casinoTopPage.tabsWithAccordion} />}
    </React.Fragment>
  );
};

export default PageContent;